import {
  CreateHomeConversation,
  DeleteHomeConversation,
  GetHomeConversationContent,
  GetHomeConversations,
  RenameHomeConversation
} from '@prophecy/queries/common/queries/homeConversation';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { QUERY_NOTIFY_PROPS_WITH_FETCHING } from '../../data/queryConstants';
import { useGraphQlMutation, useInfiniteGraphQlQuery } from '../../data/util';
import { mergeTranscripts, parseTranscriptJsonl } from './chatTranscript';
import { CONVERSATIONS_PAGE_SIZE, HOME_CHAT_QUERY_KEY, THREAD_PAGE_SIZE } from './constants';

import type { HomeChatMessages } from './chatTranscript';
import type { ResultOf, VariablesOf } from 'gql.tada';

type ConversationsPage = ResultOf<typeof GetHomeConversations>;
type ContentPage = ResultOf<typeof GetHomeConversationContent>;

export type HomeConversation = ConversationsPage['getLastNConversations'][number];

// A bare prefix invalidates every team-scoped variant below it too (react-query matches by prefix).
const CONVERSATIONS_KEY = [HOME_CHAT_QUERY_KEY, 'conversations'];
const conversationsKey = (teamUID: string | undefined) => [...CONVERSATIONS_KEY, teamUID ?? null];
const contentKey = (conversationId: string) => [HOME_CHAT_QUERY_KEY, 'content', conversationId];

export function useInvalidateHomeConversations() {
  const queryClient = useQueryClient();
  return usePersistentCallback(() => queryClient.invalidateQueries(CONVERSATIONS_KEY));
}

export function useInvalidateHomeConversationContent() {
  const queryClient = useQueryClient();
  return usePersistentCallback((conversationId: string) => queryClient.invalidateQueries(contentKey(conversationId)));
}

export function useHomeConversations(teamUID?: string) {
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteGraphQlQuery<
    ConversationsPage,
    VariablesOf<typeof GetHomeConversations>,
    ConversationsPage
  >(
    conversationsKey(teamUID),
    GetHomeConversations,
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage.getLastNConversations.length < CONVERSATIONS_PAGE_SIZE
          ? undefined
          : { offset: allPages.length * CONVERSATIONS_PAGE_SIZE }
    },
    { limit: CONVERSATIONS_PAGE_SIZE, offset: 0, teamUID }
  );

  // The server orders by updatedAt, which a rename bumps; the list keeps creation order instead.
  const conversations = useMemo(
    () => (data?.pages.flatMap((page) => page.getLastNConversations) ?? []).sort((a, b) => b.createdAt - a.createdAt),
    [data]
  );

  return { conversations, isLoading, hasNextPage: Boolean(hasNextPage), fetchNextPage, isFetchingNextPage };
}

export function useHomeConversationContent(conversationId: string | undefined) {
  const { data, isInitialLoading, isFetching, isFetchedAfterMount, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteGraphQlQuery<ContentPage, VariablesOf<typeof GetHomeConversationContent>, ContentPage>(
      contentKey(conversationId ?? ''),
      GetHomeConversationContent,
      {
        enabled: Boolean(conversationId),
        notifyOnChangeProps: [...QUERY_NOTIFY_PROPS_WITH_FETCHING],
        getNextPageParam: (lastPage, allPages) =>
          lastPage.getConversationContentPaginated.hasMore
            ? { promptOffset: allPages.length * THREAD_PAGE_SIZE }
            : undefined
      },
      { conversationId: conversationId ?? '', promptOffset: 0, promptLimit: THREAD_PAGE_SIZE }
    );

  // pages[0] holds the latest prompts, so the oldest page goes first and newer ones override it
  const messages = useMemo<HomeChatMessages>(
    () =>
      mergeTranscripts(
        [...(data?.pages ?? [])]
          .reverse()
          .map((page) => parseTranscriptJsonl(page.getConversationContentPaginated.content))
      ),
    [data]
  );

  return {
    messages,
    // A cached page predates the turns that ran since; showing it would render an empty or partial thread.
    isLoading: isInitialLoading || (isFetching && !isFetchedAfterMount),
    hasOlder: Boolean(hasNextPage),
    loadOlder: fetchNextPage,
    isLoadingOlder: isFetchingNextPage
  };
}

export function useCreateHomeConversation() {
  const invalidate = useInvalidateHomeConversations();
  const { mutateAsync, isLoading } = useGraphQlMutation(CreateHomeConversation);
  const create = usePersistentCallback(async (variables: VariablesOf<typeof CreateHomeConversation>) => {
    const result = await mutateAsync(variables);
    invalidate();
    return result.addAgentConversation;
  });
  return { create, isCreating: isLoading };
}

// `renamingId`/`deletingId` are the one conversation currently in flight (or undefined), so the
// sidebar row and the chat header — both driven by the same hook instance — can disable their
// own rename/delete controls instead of allowing a second one to fire before the first resolves.
export function useRenameHomeConversation() {
  const invalidate = useInvalidateHomeConversations();
  const { mutateAsync } = useGraphQlMutation(RenameHomeConversation);
  const [renamingId, setRenamingId] = useState<string>();
  const rename = usePersistentCallback(async (conversationId: string, name: string) => {
    setRenamingId(conversationId);
    try {
      await mutateAsync({ conversationId, name });
      invalidate();
    } catch {
      // Already shown by the mutation's default onError; callers fire and forget.
    } finally {
      setRenamingId(undefined);
    }
  });
  return { rename, renamingId };
}

export function useDeleteHomeConversation() {
  const invalidate = useInvalidateHomeConversations();
  const queryClient = useQueryClient();
  const { mutateAsync } = useGraphQlMutation(DeleteHomeConversation);
  const [deletingId, setDeletingId] = useState<string>();
  const remove = usePersistentCallback(async (conversationId: string) => {
    setDeletingId(conversationId);
    try {
      await mutateAsync({ conversationId });
      queryClient.removeQueries(contentKey(conversationId));
      invalidate();
    } finally {
      setDeletingId(undefined);
    }
  });
  return { remove, deletingId };
}
